import React, { Ref, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import IconAvatar, {
  IconAvatarProps,
} from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import UserAvatar, {
  UserAvatarProps,
} from 'shared/components/vxl/avatar/UserAvatar/UserAvatar';
import { Size } from 'shared/components/vxl/avatar/UserAvatar/UserAvatar.helpers';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import NoteInput, {
  NoteInputVariant,
} from 'shared/components/vxl/input/NoteInput/NoteInput';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { isWeb } from 'shared/constants/platform';
import { VXLComponent, VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

export interface TransferFieldAccessoryProps extends VXLComponent {
  /**
   * The type of user/icon component to use.
   * Must be one of VXLComponentId.ICON_AVATAR or VXLComponentId.USER_AVATAR.
   */
  name: VXLComponentId.ICON_AVATAR | VXLComponentId.USER_AVATAR;

  /**
   * The props to pass to the user/icon component.
   * Must match the type of user/icon component specified in `name`.
   */
  props: IconAvatarProps | UserAvatarProps;
}

export interface TransferFieldProps {
  focusRef?: Ref<View>;
  /**
   * The main text displayed on the menu.
   */
  primaryText: string;

  /**
   * The secondary text displayed on the menu. Optional.
   */
  secondaryText?: string;

  /**
   * The label text displayed on the left side of the menu.
   */
  labelText: string;

  /**
   * If true, the menu is disabled and cannot be pressed.
   */
  isDisabled?: boolean;

  /**
   * The function to call when the menu is pressed.
   */
  onPress: () => void;

  /**
   * The avatar accessory to display on the menu.
   */
  avatarAccessory: TransferFieldAccessoryProps;

  /**
   * Determines whether to show the HairLineDivider component.
   * Defaults to true.
   */
  showDivider?: boolean;

  /**
   * Determines whether to show the Chevron Down icon.
   * Defaults to true.
   */
  showChevron?: boolean;

  /**
   * Determines whether to show the Chevron Down icon or Chevron Up icon.
   * Defaults to true.
   */
  isCollapsed?: boolean;

  /**
   * Determines whether the primary text is editable.
   * Defaults to false.
   */
  isEditable?: boolean;

  /**
   * The function to call when the text is changed.
   */
  onChangeText?: (text: string) => void;

  /**
   * The placeholder text for the input field.
   */
  placeholderText?: string;
}

const TransferField = ({
  focusRef,
  primaryText,
  secondaryText,
  labelText,
  isDisabled = false,
  onPress,
  onChangeText,
  avatarAccessory,
  showDivider = true,
  showChevron = true,
  isCollapsed = true,
  isEditable = false,
  placeholderText,
}: TransferFieldProps) => {
  const { name, props: avatarProps } = avatarAccessory;
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);
  const handleHoverIn = () => setIsHovered(true);
  const handleHoverOut = () => setIsHovered(false);

  const backgroundColor = (pressed: boolean) => {
    if (isHovered) {
      return theme.color.interactive.secondaryHovered;
    }
    if (pressed) {
      return theme.color.interactive.secondaryPressed;
    }
    return theme.color.interactive.secondary;
  };

  const textColor = isDisabled
    ? theme.color.interactive.disabled.text
    : theme.color.text.primary;

  const defaultIconAvatarProps = {
    variant: 'MD' as IconAvatarProps['variant'],
  };

  const defaultUserAvatarProps = {
    userAvatarSize: Size.Medium as UserAvatarProps['userAvatarSize'],
  };

  return (
    <Pressable
      ref={focusRef}
      disabled={isDisabled}
      onPress={onPress}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      accessible={false}
      accessibilityRole="button"
      testID={
        labelText === 'From:' ? 'From:-selectAccount' : 'To:-selectAccount'
      }
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      style={({ pressed }) => [
        styles.transferFieldContainer,
        { backgroundColor: backgroundColor(pressed) },
        isFocused && styles.focused,
        isWeb && { outlineWidth: 0 },
      ]}
    >
      {/* Revert: labelTextWrapper and cardAndDividerWrapper are direct children of Pressable */}
      <View style={styles.labelTextWrapper}>
        <BodySmBoldText
          style={
            isDisabled
              ? styles.disabledLabelTextStyle
              : styles.enabledLabelTextStyle
          }
          numberOfLines={1}
        >
          {labelText}
        </BodySmBoldText>
      </View>
      <View style={styles.cardAndDividerWrapper}>
        <View style={styles.innerCardContainer} accessible={false}>
          {name === VXLComponentId.ICON_AVATAR ? (
            <IconAvatar
              {...defaultIconAvatarProps}
              {...(avatarProps as IconAvatarProps)}
            />
          ) : null}
          {name === VXLComponentId.USER_AVATAR ? (
            <View accessible accessibilityLabel="user-avatar-wrapper">
              <UserAvatar
                {...defaultUserAvatarProps}
                {...(avatarProps as UserAvatarProps)}
              />
            </View>
          ) : null}

          <View style={styles.primaryAndSecondaryTextWrapper}>
            {isEditable ? (
              <NoteInput
                placeholderText={placeholderText ?? ''}
                value={primaryText}
                variant={NoteInputVariant.SHORT}
                onChangeText={onChangeText}
                backgroundColor={backgroundColor(pressed)}
              />
            ) : (
              <AmplitudeMask>
                <BodyBaseText
                  style={
                    isDisabled
                      ? {
                          ...styles.disabledPrimaryText,
                        }
                      : {
                          ...styles.enabledPrimaryText,
                        }
                  }
                  numberOfLines={1}
                >
                  {primaryText}
                </BodyBaseText>
              </AmplitudeMask>
            )}
            {secondaryText ? (
              <AmplitudeMask>
                <BodyXSText
                  style={
                    isDisabled
                      ? {
                          ...styles.disabledSecondaryText,
                        }
                      : {
                          ...styles.enabledSecondaryText,
                        }
                  }
                  numberOfLines={1}
                >
                  {secondaryText}
                </BodyXSText>
              </AmplitudeMask>
            ) : null}
          </View>
          {showChevron ? (
            <Icon
              iconSize="MD"
              iconAccessibilityLabel={
                isCollapsed ? 'chevron-down-icon' : 'chevron-up-icon'
              }
              iconKey={isCollapsed ? 'CHEVRON_DOWN' : 'CHEVRON_UP'}
              iconColor={textColor}
            />
          ) : null}
        </View>
        {showDivider ? <HairLineDivider /> : null}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  cardAndDividerWrapper: {
    flex: 1,
    paddingLeft: theme.spacing.m,
  },
  disabledLabelTextStyle: {
    color: theme.color.interactive.disabled.text,
  },
  disabledPrimaryText: {
    color: theme.color.interactive.disabled.text,
    flexShrink: 1,
    textAlign: 'left',
  },
  disabledSecondaryText: {
    color: theme.color.interactive.disabled.text,
    flexShrink: 1,
    textAlign: 'left',
  },
  enabledLabelTextStyle: {
    color: theme.color.text.primary,
  },
  enabledPrimaryText: {
    color: theme.color.text.primary,
    flexShrink: 1,
    textAlign: 'left',
  },
  enabledSecondaryText: {
    color: theme.color.text.secondary,
    flexShrink: 1,
    textAlign: 'left',
  },
  focused: {
    outlineColor: theme.color.interactive.focused.border,
  },
  innerCardContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingRight: theme.spacing.xs,
    paddingVertical: theme.spacing.m,
  },
  labelTextWrapper: {
    maxWidth: 50,
    minWidth: 37,
  },
  primaryAndSecondaryTextWrapper: {
    alignItems: 'flex-start',
    flex: 1,
    paddingLeft: theme.spacing.m,
  },
  transferFieldContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.interactive.secondary,
    flexDirection: 'row',
    paddingHorizontal: theme.spacing.m,
  },
});

export default TransferField;
